import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { Dashboard, Widget, WidgetAPI } from 'api/dashboard';
import {
  CreateWidgetPayload,
  EditWidgetPayload,
  PreviewWidgetPayload,
  SimpleFilter,
  ChartOptions,
  DrillDownPayload,
  WidgetResponse,
  SimpleSearchFilter,
  WidgetOrder,
  DrillDownExportResponse,
} from 'api/dashboard/widget';
import { Project } from 'api/project';
import { getDashboardQueryKey } from './dashboard';

type ResolveWidgetQueryKey = [string, number];
const getResolveWidgetQueryKey = (widgetId: number): ResolveWidgetQueryKey => [
  'resolve-widget',
  widgetId,
];

type QueryWidgetKey = [string, number];
export const getQueryWidgetKey = (widgetId: number): QueryWidgetKey => [
  'widget',
  widgetId,
];

export const getResolveMultipleWidgetsQueryKey = (
  widgetIds: number[],
  simpleSearchFilter: SimpleSearchFilter | null = null,
): (string | number | SimpleSearchFilter | null)[] => [
  'resolve-multiple',
  ...widgetIds,
  simpleSearchFilter,
];

const useResolveWidgetQuery = (
  widgetId: number,
  options: UseQueryOptions<
    ChartOptions,
    unknown,
    ChartOptions,
    ResolveWidgetQueryKey
  > = {},
): UseQueryResult<ChartOptions> => {
  return useQuery(
    getResolveWidgetQueryKey(widgetId),
    () => WidgetAPI.resolveWidget(widgetId),
    { ...options, retry: 1, refetchOnWindowFocus: false },
  );
};

const useResolveMultipleWidgetsQuery = (
  dashboardId: number,
  widgetIds: number[] = [],
  simpleSearchFilter: SimpleSearchFilter | null = null,
  options: UseQueryOptions<
    WidgetResponse[],
    unknown,
    WidgetResponse[],
    (string | number | SimpleSearchFilter | null)[]
  > = {},
): UseQueryResult<WidgetResponse[]> => {
  return useQuery(
    getResolveMultipleWidgetsQueryKey(widgetIds, simpleSearchFilter),
    () =>
      WidgetAPI.resolveMultipleWidgets(
        dashboardId,
        widgetIds,
        simpleSearchFilter,
      ),
    { ...options, retry: 1, refetchOnWindowFocus: false },
  );
};

const useWidgetQuery = <WidgetType = Widget>(
  widgetId: number,
  options: UseQueryOptions<
    WidgetType,
    unknown,
    WidgetType,
    QueryWidgetKey
  > = {},
): UseQueryResult<WidgetType> => {
  return useQuery(
    getQueryWidgetKey(widgetId),
    () => WidgetAPI.getWidget<WidgetType>(widgetId),
    {
      ...options,
    },
  );
};

export const previewWidgetQueryKey = (): [string] => ['preview-widget'];

const usePreviewWidgetQuery = (
  payload: PreviewWidgetPayload,
): UseQueryResult<ChartOptions> => {
  return useQuery([previewWidgetQueryKey(), payload], () =>
    WidgetAPI.previewWidget(payload),
  );
};

const useDrillWidgetMutation = (widgetId: number) => {
  return useMutation<Project[], unknown, SimpleFilter>((payload) =>
    WidgetAPI.drillWidget(widgetId, payload),
  );
};

const useDrillWidgetMutationV2 = (widgetId: number) => {
  return useMutation<Project[], unknown, DrillDownPayload>((payload) =>
    WidgetAPI.drillWidgetV2(widgetId, payload),
  );
};

const useUpdateWidgetOrderMutation = (
  dashbaordId: number,
  options: UseMutationOptions<void, unknown, WidgetOrder[]> = {},
): UseMutationResult<void, unknown, WidgetOrder[]> =>
  useMutation((payload) => WidgetAPI.updateWidgetOrder(dashbaordId, payload), {
    ...options,
  });

const useCreateWidgetMutation = (): UseMutationResult<
  Widget,
  unknown,
  CreateWidgetPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<Widget, unknown, CreateWidgetPayload>(
    (payload) => WidgetAPI.create(payload),
    {
      onSuccess: async (newWidget, { dashboardId }) => {
        const key = getDashboardQueryKey(Number(dashboardId));
        const previous = queryClient.getQueryData<Dashboard>(key);

        if (previous) {
          queryClient.setQueryData<Dashboard>(key, () => {
            return {
              ...previous,
              widgets: [...previous.widgets, newWidget],
            };
          });
        } else {
          await queryClient.invalidateQueries(key);
        }
      },
    },
  );
};

export type EditWidgetMutationPayload = EditWidgetPayload &
  Pick<Widget, 'widgetId'>;

const useEditWidgetMutation = (): UseMutationResult<
  Widget,
  unknown,
  EditWidgetMutationPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<Widget, unknown, EditWidgetMutationPayload>(
    ({ widgetId, ...payload }) => WidgetAPI.editWidget(widgetId, payload),
    {
      onSuccess: async (res) => {
        await queryClient.invalidateQueries(
          getDashboardQueryKey(res.dashboardId),
        );
        await queryClient.invalidateQueries(
          getResolveWidgetQueryKey(res.widgetId),
        );
      },
    },
  );
};

const useDeleteWidgetMutation = (): UseMutationResult<
  void,
  unknown,
  { widgetId: number; dashboardId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation(({ widgetId }) => WidgetAPI.deleteWidget(widgetId), {
    onSuccess: async (_, { dashboardId, widgetId }) => {
      const key = getDashboardQueryKey(Number(dashboardId));
      const previous = queryClient.getQueryData<Dashboard>(key);

      if (previous) {
        const widgets = previous.widgets.filter(
          (widget) => widget.widgetId !== widgetId,
        );

        queryClient.setQueryData<Dashboard>(key, () => {
          return {
            ...previous,
            widgets,
          };
        });
      } else {
        await queryClient.invalidateQueries(key);
      }
    },
  });
};

const useWidgetExportsMutation = (
  widgetId: number,
  options: UseMutationOptions<
    DrillDownExportResponse,
    unknown,
    DrillDownPayload
  > = {},
): UseMutationResult<DrillDownExportResponse, unknown, DrillDownPayload> => {
  return useMutation(
    (payload) => WidgetAPI.drillDownExport(widgetId, payload),
    {
      ...options,
    },
  );
};

export {
  useCreateWidgetMutation,
  useDeleteWidgetMutation,
  useResolveWidgetQuery,
  useResolveMultipleWidgetsQuery,
  useWidgetQuery,
  useEditWidgetMutation,
  useDrillWidgetMutation,
  getResolveWidgetQueryKey,
  usePreviewWidgetQuery,
  useDrillWidgetMutationV2,
  useUpdateWidgetOrderMutation,
  useWidgetExportsMutation,
};
